import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Send, X, Download, Bell, Archive, RotateCcw } from "lucide-react";
import api, { API, formatApiError } from "../api";
import { EXPERTISE, PROVINCES, APPLICATION_STATUS_LABELS } from "../lib/constants";

const TABS = ["Overzicht", "Vacatures", "Kandidaten", "Inleners", "Sollicitaties", "Match", "Nieuwsbrief", "E-maillog"];

export default function AdminDashboard() {
  const [tab, setTab] = useState("Overzicht");
  const [stats, setStats] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [clients, setClients] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [showVacForm, setShowVacForm] = useState(false);
  const [editVac, setEditVac] = useState(null);
  const [matchVacId, setMatchVacId] = useState("");
  const [matches, setMatches] = useState([]);
  const [proposeClientId, setProposeClientId] = useState("");
  const [newsletter, setNewsletter] = useState({ subject: "", body: "", expertise_category: "" });
  const [detailModal, setDetailModal] = useState(null); // { type: 'candidate'|'client'|'application', data }
  const [detailLoading, setDetailLoading] = useState(false);

  const openCandidateDetail = async (userId) => {
    setDetailLoading(true);
    setDetailModal({ type: "candidate", data: null });
    try {
      const { data } = await api.get(`/admin/candidates/${userId}`);
      setDetailModal({ type: "candidate", data });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDetailModal(null);
    }
    setDetailLoading(false);
  };
  const openClientDetail = async (userId) => {
    setDetailLoading(true);
    setDetailModal({ type: "client", data: null });
    try {
      const { data } = await api.get(`/admin/clients/${userId}`);
      setDetailModal({ type: "client", data });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDetailModal(null);
    }
    setDetailLoading(false);
  };
  const openApplicationDetail = async (appId) => {
    setDetailLoading(true);
    setDetailModal({ type: "application", data: null });
    try {
      const { data } = await api.get(`/admin/applications/${appId}`);
      setDetailModal({ type: "application", data });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDetailModal(null);
    }
    setDetailLoading(false);
  };
  const openEmailDetail = async (eid) => {
    setDetailLoading(true);
    setDetailModal({ type: "email", data: null });
    try {
      const { data } = await api.get(`/admin/email-log/${eid}`);
      setDetailModal({ type: "email", data });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDetailModal(null);
    }
    setDetailLoading(false);
  };
  // Vacatures tab state
  const [vacFilter, setVacFilter] = useState({ q: "", expertise: "", status: "open" });
  const [showExpired, setShowExpired] = useState(false);
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [archivedNotifs, setArchivedNotifs] = useState([]);
  const [showNotifArchive, setShowNotifArchive] = useState(false);

  const loadNotifications = async () => {
    try {
      const [a, ar] = await Promise.all([
        api.get("/admin/notifications?status=active&limit=50"),
        api.get("/admin/notifications?status=archived&limit=100"),
      ]);
      setNotifications(a.data);
      setArchivedNotifs(ar.data);
    } catch (_) { /* ignore */ }
  };
  const archiveNotification = async (nid) => {
    try {
      await api.put(`/admin/notifications/${nid}/archive`);
      loadNotifications();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const restoreNotification = async (nid) => {
    try {
      await api.put(`/admin/notifications/${nid}/restore`);
      loadNotifications();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const loadAll = async () => {
    try {
      const [s, c, cl, vOpen, vFilled, vExpired, a, e] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/candidates"),
        api.get("/admin/clients"),
        api.get("/vacancies?status=open&limit=500"),
        api.get("/vacancies?status=ingevuld&limit=500"),
        api.get("/vacancies?status=verlopen&limit=500"),
        api.get("/admin/applications"),
        api.get("/admin/email-log?limit=100"),
      ]);
      setStats(s.data); setCandidates(c.data); setClients(cl.data);
      setVacancies([...vOpen.data, ...vFilled.data, ...vExpired.data]);
      setApplications(a.data); setEmailLog(e.data);
      loadNotifications();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { loadAll(); }, []);

  const saveVacancy = async (form) => {
    try {
      if (editVac) await api.put(`/vacancies/${editVac.id}`, form);
      else await api.post("/vacancies", form);
      toast.success("Opgeslagen.");
      setShowVacForm(false); setEditVac(null); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteVacancy = async (id) => {
    if (!window.confirm("Vacature verwijderen?")) return;
    await api.delete(`/vacancies/${id}`);
    toast.success("Verwijderd.");
    loadAll();
  };

  const loadMatches = async (vid) => {
    if (!vid) { setMatches([]); return; }
    const { data } = await api.get(`/admin/match/${vid}`);
    setMatches(data);
  };

  const propose = async (candId) => {
    if (!proposeClientId) { toast.error("Kies eerst een inlener."); return; }
    try {
      await api.post("/proposals", {
        client_id: proposeClientId,
        candidate_id: candId,
        vacancy_id: matchVacId,
      });
      toast.success("Voorgesteld aan inlener.");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendNewsletter = async () => {
    if (!newsletter.subject || !newsletter.body) return toast.error("Onderwerp en tekst zijn vereist.");
    try {
      const { data } = await api.post("/admin/newsletter", {
        subject: newsletter.subject,
        body: newsletter.body,
        expertise_category: newsletter.expertise_category || null,
      });
      toast.success(`Verstuurd naar ${data.count} kandidaten.`);
      setNewsletter({ subject: "", body: "", expertise_category: "" });
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <section className="py-10">
      <div className="ok-container">
        <div className="ok-eyebrow mb-2">Beheer</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Admin dashboard</h1>

        <div className="mt-8 border-b border-[#e2e8f0] overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {TABS.map((t) => (
              <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
                className={`px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === t ? "border-[#01696f] text-[#01696f] font-semibold" : "border-transparent text-[#4a5d5e] hover:text-[#0a191a]"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {tab === "Overzicht" && (
            <div className="space-y-10">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Stat label="Kandidaten" value={stats.candidates || 0} />
                <Stat label="Inleners" value={stats.clients || 0} />
                <Stat label="Open vacatures" value={stats.vacancies_open || 0} />
                <Stat label="Sollicitaties" value={stats.applications || 0} />
                <Stat label="Voorstellen" value={stats.proposals || 0} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Bell size={18} className="text-[#01696f]" />
                    <h2 className="font-display text-xl font-semibold">
                      {showNotifArchive ? "Archief notificaties" : "Notificaties"}
                    </h2>
                    {!showNotifArchive && notifications.length > 0 && (
                      <span className="bg-[#01696f] text-white text-xs font-semibold rounded-full px-2 py-0.5 ml-1">
                        {notifications.length}
                      </span>
                    )}
                  </div>
                  <button
                    data-testid="toggle-notif-archive"
                    onClick={() => setShowNotifArchive(!showNotifArchive)}
                    className="inline-flex items-center gap-2 text-sm text-[#01696f] hover:underline"
                  >
                    {showNotifArchive ? (<>← Terug naar actief</>) : (<><Archive size={14} /> Archief bekijken ({archivedNotifs.length})</>)}
                  </button>
                </div>

                {!showNotifArchive ? (
                  <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
                    {notifications.length === 0 && (
                      <p className="p-6 text-sm text-[#7b8e8f]">Geen actieve notificaties. Nieuwe aanmeldingen en sollicitaties verschijnen hier automatisch.</p>
                    )}
                    {notifications.map((n) => (
                      <div key={n.id} data-testid={`notification-${n.id}`} className="p-4 flex items-start justify-between gap-4 hover:bg-[#f7f6f2]">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <NotifBadge type={n.type} />
                            <span className="text-xs text-[#7b8e8f]">{n.created_at?.slice(0, 19).replace("T", " ")}</span>
                          </div>
                          <div className="font-semibold">{n.title}</div>
                          <div className="text-sm text-[#4a5d5e] mt-1">{n.message}</div>
                        </div>
                        <button
                          data-testid={`archive-notif-${n.id}`}
                          onClick={() => archiveNotification(n.id)}
                          className="shrink-0 p-2 text-[#4a5d5e] hover:text-[#01696f] hover:bg-white rounded-sm"
                          title="Sluiten (archiveren)"
                          aria-label="Notificatie sluiten"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]" data-testid="notif-archive-list">
                    {archivedNotifs.length === 0 && (
                      <p className="p-6 text-sm text-[#7b8e8f]">Het archief is leeg.</p>
                    )}
                    {archivedNotifs.map((n) => (
                      <div key={n.id} className="p-4 flex items-start justify-between gap-4 hover:bg-[#f7f6f2] opacity-80">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <NotifBadge type={n.type} />
                            <span className="text-xs text-[#7b8e8f]">{n.created_at?.slice(0, 19).replace("T", " ")}</span>
                            {n.archived_at && (
                              <span className="text-xs text-[#7b8e8f]">· gearchiveerd {n.archived_at.slice(0, 10)}</span>
                            )}
                          </div>
                          <div className="font-semibold">{n.title}</div>
                          <div className="text-sm text-[#4a5d5e] mt-1">{n.message}</div>
                        </div>
                        <button
                          data-testid={`restore-notif-${n.id}`}
                          onClick={() => restoreNotification(n.id)}
                          className="shrink-0 p-2 text-[#4a5d5e] hover:text-[#01696f] hover:bg-white rounded-sm"
                          title="Terugzetten naar actief"
                          aria-label="Terugzetten"
                        >
                          <RotateCcw size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "Vacatures" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="font-display text-xl font-semibold">Alle vacatures</h2>
                <button data-testid="new-vacancy-btn" onClick={() => { setEditVac(null); setShowVacForm(true); }}
                  className="inline-flex items-center gap-2 bg-[#01696f] text-white px-4 py-2 rounded-sm text-sm hover:bg-[#015257]">
                  <Plus size={14} /> Nieuwe vacature
                </button>
              </div>
              {showVacForm && <VacancyForm initial={editVac} onClose={() => { setShowVacForm(false); setEditVac(null); }} onSave={saveVacancy} />}

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
                <input
                  data-testid="vac-filter-search"
                  placeholder="Zoek op titel of beschrijving"
                  className="px-3 py-2 border border-[#e2e8f0] text-sm bg-white"
                  value={vacFilter.q}
                  onChange={(e) => setVacFilter({ ...vacFilter, q: e.target.value })}
                />
                <select
                  data-testid="vac-filter-expertise"
                  className="px-3 py-2 border border-[#e2e8f0] text-sm bg-white"
                  value={vacFilter.expertise}
                  onChange={(e) => setVacFilter({ ...vacFilter, expertise: e.target.value })}
                >
                  <option value="">Alle expertises</option>
                  {EXPERTISE.map((x) => <option key={x}>{x}</option>)}
                </select>
                <select
                  data-testid="vac-filter-status"
                  className="px-3 py-2 border border-[#e2e8f0] text-sm bg-white"
                  value={vacFilter.status}
                  onChange={(e) => { setVacFilter({ ...vacFilter, status: e.target.value }); setShowExpired(e.target.value === "verlopen"); }}
                >
                  <option value="">Alle statussen</option>
                  <option value="open">Open</option>
                  <option value="ingevuld">Ingevuld</option>
                  <option value="verlopen">Verlopen</option>
                </select>
                <button
                  data-testid="vac-filter-clear"
                  onClick={() => { setVacFilter({ q: "", expertise: "", status: "open" }); setShowExpired(false); }}
                  className="px-3 py-2 border border-[#e2e8f0] text-sm hover:border-[#01696f]"
                >
                  Filters wissen
                </button>
              </div>

              {(() => {
                const filtered = vacancies.filter((v) => {
                  if (showExpired) {
                    if (v.status !== "verlopen") return false;
                  } else if (vacFilter.status && v.status !== vacFilter.status) {
                    return false;
                  }
                  if (vacFilter.expertise && v.expertise_category !== vacFilter.expertise) return false;
                  if (vacFilter.q) {
                    const needle = vacFilter.q.toLowerCase();
                    if (!(v.title || "").toLowerCase().includes(needle)
                        && !(v.description || "").toLowerCase().includes(needle)
                        && !(v.location || "").toLowerCase().includes(needle)) return false;
                  }
                  return true;
                });
                return (
                  <>
                    <div className="text-xs text-[#4a5d5e] mb-3" data-testid="vac-count">
                      {filtered.length} vacatures gevonden
                    </div>
                    <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
                      {filtered.length === 0 && <p className="p-6 text-sm text-[#7b8e8f]">Geen vacatures gevonden.</p>}
                      {filtered.map((v) => (
                        <div
                          key={v.id}
                          onClick={() => { setEditVac(v); setShowVacForm(true); }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === "Enter") { setEditVac(v); setShowVacForm(true); } }}
                          data-testid={`vac-row-${v.id}`}
                          className="p-4 flex items-center justify-between hover:bg-[#f7f6f2] cursor-pointer transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{v.title}</div>
                            <div className="text-xs text-[#4a5d5e]">
                              {v.expertise_category} · {v.location} ·{" "}
                              <span className={
                                v.status === "open" ? "text-[#01696f] font-semibold"
                                : v.status === "verlopen" ? "text-[#d32f2f]"
                                : ""
                              }>{v.status}</span>
                            </div>
                          </div>
                          <div className="flex gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <button data-testid={`edit-vac-${v.id}`} onClick={() => { setEditVac(v); setShowVacForm(true); }} className="p-2 text-[#4a5d5e] hover:text-[#01696f]" title="Bewerken"><Edit size={14} /></button>
                            <button data-testid={`del-vac-${v.id}`} onClick={() => deleteVacancy(v.id)} className="p-2 text-[#d32f2f]" title="Verwijderen"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}

              {!showExpired && (
                <button
                  data-testid="show-expired-btn"
                  onClick={() => { setShowExpired(true); setVacFilter({ ...vacFilter, status: "verlopen" }); }}
                  className="mt-6 w-full border border-[#0a191a] py-3 rounded-sm text-sm hover:bg-[#0a191a] hover:text-white"
                >
                  Verlopen vacatures bekijken ({vacancies.filter((v) => v.status === "verlopen").length})
                </button>
              )}
            </div>
          )}

          {tab === "Kandidaten" && (
            <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]" data-testid="candidates-list">
              {candidates.map((c) => (
                <button
                  key={c.id}
                  data-testid={`candidate-row-${c.user_id}`}
                  onClick={() => openCandidateDetail(c.user_id)}
                  className="w-full text-left p-4 grid grid-cols-1 md:grid-cols-6 gap-2 items-center hover:bg-[#f7f6f2] transition-colors"
                >
                  <div className="md:col-span-2 font-semibold">{c.first_name} {c.last_name}</div>
                  <div className="text-xs text-[#4a5d5e]">{(c.expertise_categories && c.expertise_categories.length > 0) ? c.expertise_categories.join(", ") : c.expertise_category}</div>
                  <div className="text-xs text-[#4a5d5e]">{c.years_experience} jaar</div>
                  <div className="text-xs text-[#4a5d5e]">€ {c.hourly_rate || "n.v.t."}/u</div>
                  <div className="text-xs text-[#4a5d5e] truncate">{c.email}</div>
                </button>
              ))}
              {candidates.length === 0 && <p className="p-6 text-sm text-[#7b8e8f]">Geen kandidaten.</p>}
            </div>
          )}

          {tab === "Inleners" && (
            <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
              {clients.map((c) => (
                <button
                  key={c.id}
                  data-testid={`client-row-${c.user_id}`}
                  onClick={() => openClientDetail(c.user_id)}
                  className="w-full text-left p-4 grid grid-cols-1 md:grid-cols-4 gap-2 hover:bg-[#f7f6f2] transition-colors"
                >
                  <div className="font-semibold">{c.organization_name}</div>
                  <div className="text-xs text-[#4a5d5e]">{c.organization_type}</div>
                  <div className="text-xs text-[#4a5d5e]">{c.contact_name}</div>
                  <div className="text-xs text-[#4a5d5e] truncate">{c.email}</div>
                </button>
              ))}
              {clients.length === 0 && <p className="p-6 text-sm text-[#7b8e8f]">Geen inleners.</p>}
            </div>
          )}

          {tab === "Sollicitaties" && (
            <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
              {applications.map((a) => (
                <div
                  key={a.id}
                  onClick={() => openApplicationDetail(a.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") openApplicationDetail(a.id); }}
                  data-testid={`application-row-${a.id}`}
                  className="p-4 grid grid-cols-1 md:grid-cols-6 gap-2 items-center hover:bg-[#f7f6f2] cursor-pointer transition-colors"
                >
                  <div className="md:col-span-2 font-semibold">{a.candidate_name}</div>
                  <div className="text-xs md:col-span-2 truncate">{a.vacancy_title}</div>
                  <div className="text-xs text-[#4a5d5e]">{a.submitted_date?.slice(0, 10)}</div>
                  <div className="flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                    <span className="ok-chip text-xs">{APPLICATION_STATUS_LABELS[a.status] || a.status}</span>
                    <a
                      href={`${API}/resume/download/${a.candidate_user_id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`app-cv-${a.id}`}
                      className="inline-flex items-center gap-1 text-[#01696f] hover:underline text-xs font-medium whitespace-nowrap"
                      title="CV downloaden"
                    >
                      <Download size={14} /> CV
                    </a>
                  </div>
                </div>
              ))}
              {applications.length === 0 && <p className="p-6 text-sm text-[#7b8e8f]">Geen sollicitaties.</p>}
            </div>
          )}

          {tab === "Match" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-[#4a5d5e]">Kies vacature</label>
                  <select data-testid="match-vacancy" className="mt-2 w-full p-2.5 border border-[#e2e8f0]"
                    value={matchVacId} onChange={(e) => { setMatchVacId(e.target.value); loadMatches(e.target.value); }}>
                    <option value="">Kies vacature</option>
                    {vacancies.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-[#4a5d5e]">Aanbieden aan inlener</label>
                  <select data-testid="match-client" className="mt-2 w-full p-2.5 border border-[#e2e8f0]"
                    value={proposeClientId} onChange={(e) => setProposeClientId(e.target.value)}>
                    <option value="">Kies inlener</option>
                    {clients.map((c) => <option key={c.id} value={c.user_id}>{c.organization_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
                {matches.length === 0 && <p className="p-6 text-sm text-[#7b8e8f]">Geen matches (kies eerst een vacature).</p>}
                {matches.map((m) => (
                  <div key={m.id} className="p-4 flex items-center justify-between hover:bg-[#f7f6f2]">
                    <div>
                      <div className="font-semibold">{m.first_name} {m.last_name}</div>
                      <div className="text-xs text-[#4a5d5e]">{m.expertise_category} · {m.years_experience} jaar · € {m.hourly_rate || "n.v.t."}/u</div>
                    </div>
                    <button data-testid={`propose-${m.user_id}`} onClick={() => propose(m.user_id)}
                      className="inline-flex items-center gap-2 bg-[#01696f] text-white px-3 py-2 rounded-sm text-sm hover:bg-[#015257]">
                      <Send size={14} /> Aanbieden
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "Nieuwsbrief" && (
            <div className="max-w-2xl space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-[#4a5d5e]">Doelgroep (expertise, leeg = iedereen)</label>
                <select data-testid="nl-expertise" className="mt-2 w-full p-2.5 border border-[#e2e8f0]"
                  value={newsletter.expertise_category} onChange={(e) => setNewsletter({ ...newsletter, expertise_category: e.target.value })}>
                  <option value="">Iedereen die geabonneerd is</option>
                  {EXPERTISE.map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
              <input data-testid="nl-subject" className="w-full p-2.5 border border-[#e2e8f0]" placeholder="Onderwerp"
                value={newsletter.subject} onChange={(e) => setNewsletter({ ...newsletter, subject: e.target.value })} />
              <textarea data-testid="nl-body" rows={8} className="w-full p-2.5 border border-[#e2e8f0]" placeholder="Bericht…"
                value={newsletter.body} onChange={(e) => setNewsletter({ ...newsletter, body: e.target.value })} />
              <button data-testid="nl-send" onClick={sendNewsletter}
                className="bg-[#01696f] text-white px-5 py-2.5 rounded-sm hover:bg-[#015257]">Verstuur nieuwsbrief</button>
            </div>
          )}

          {tab === "E-maillog" && (
            <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]" data-testid="email-log">
              {emailLog.map((e) => (
                <button
                  key={e.id}
                  data-testid={`email-row-${e.id}`}
                  onClick={() => openEmailDetail(e.id)}
                  className="w-full text-left p-4 hover:bg-[#f7f6f2] transition-colors"
                >
                  <div className="text-xs text-[#7b8e8f]">{e.sent_at?.slice(0, 19).replace("T", " ")} · {e.kind}</div>
                  <div className="font-semibold text-sm">{e.subject}</div>
                  <div className="text-xs text-[#4a5d5e]">naar: {e.to}</div>
                </button>
              ))}
              {emailLog.length === 0 && <p className="p-6 text-sm text-[#7b8e8f]">Nog geen e-mails verstuurd.</p>}
            </div>
          )}
        </div>
      </div>

      {detailModal && (
        <DetailModal
          state={detailModal}
          loading={detailLoading}
          onClose={() => setDetailModal(null)}
        />
      )}
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#e2e8f0] p-5">
      <div className="text-xs uppercase tracking-wider text-[#4a5d5e]">{label}</div>
      <div className="font-display text-3xl font-bold text-[#01696f] mt-2">{value}</div>
    </div>
  );
}

function NotifBadge({ type }) {
  const map = {
    candidate_signup: { label: "Kandidaat", color: "bg-[#e6f0f1] text-[#01696f] border-[#cfe2e3]" },
    client_signup: { label: "Inlener", color: "bg-[#f7f6f2] text-[#0a191a] border-[#e2e8f0]" },
    application: { label: "Sollicitatie", color: "bg-[#fff6e6] text-[#8a5b00] border-[#f5dca8]" },
  };
  const m = map[type] || { label: type, color: "bg-[#f7f6f2] text-[#4a5d5e] border-[#e2e8f0]" };
  return (
    <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${m.color}`}>
      {m.label}
    </span>
  );
}

function DetailModal({ state, loading, onClose }) {
  const { type, data } = state;
  const title = type === "candidate" ? "Kandidaatprofiel"
    : type === "client" ? "Inlenerprofiel"
    : type === "email" ? "E-mailbericht"
    : "Sollicitatie";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="detail-modal"
    >
      <div
        className="bg-white max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto border border-[#e2e8f0] rounded-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#e2e8f0] px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">{title}</h2>
          <button onClick={onClose} data-testid="detail-modal-close" className="p-1 hover:bg-[#f7f6f2] rounded-sm" aria-label="Sluiten">
            <X size={18} />
          </button>
        </div>
        <div className="p-6">
          {loading || !data ? (
            <p className="text-sm text-[#4a5d5e]">Laden...</p>
          ) : type === "candidate" ? (
            <CandidateDetailBody c={data} />
          ) : type === "client" ? (
            <ClientDetailBody c={data} />
          ) : type === "email" ? (
            <EmailDetailBody e={data} />
          ) : (
            <ApplicationDetailBody a={data} />
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-[#f0f3f4] last:border-0">
      <div className="text-xs uppercase tracking-wider font-semibold text-[#4a5d5e]">{label}</div>
      <div className="col-span-2 text-sm">{value || <span className="text-[#7b8e8f]">n.v.t.</span>}</div>
    </div>
  );
}

function CandidateDetailBody({ c }) {
  const expertise = (c.expertise_categories && c.expertise_categories.length > 0)
    ? c.expertise_categories.join(", ")
    : c.expertise_category;
  return (
    <div className="space-y-6">
      <div>
        <div className="ok-eyebrow mb-1">Persoonlijk</div>
        <Row label="Naam" value={`${c.first_name} ${c.last_name}`} />
        <Row label="E-mail" value={<a href={`mailto:${c.email}`} className="text-[#01696f] hover:underline">{c.email}</a>} />
        <Row label="Telefoon" value={c.phone} />
        <Row label="LinkedIn" value={c.linkedin_url ? <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="text-[#01696f] hover:underline">{c.linkedin_url}</a> : null} />
        <Row label="Aangemeld op" value={c.created_at?.slice(0, 10)} />
        <Row label="Status" value={c.status} />
      </div>
      <div>
        <div className="ok-eyebrow mb-1">Opdrachtvoorkeuren</div>
        <Row label="Expertise" value={expertise} />
        <Row label="Ervaring" value={`${c.years_experience} jaar`} />
        <Row label="Contractvorm" value={c.contract_type} />
        <Row label="Provincievoorkeur" value={(c.province_preference || []).join(", ")} />
        <Row label="Beschikbaar vanaf" value={c.available_from} />
        <Row label="Uurtarief" value={c.hourly_rate ? `€ ${c.hourly_rate}/u` : null} />
        <Row label="Nieuwsbrief" value={c.newsletter_subscribed ? "Ja" : "Nee"} />
      </div>
      {c.motivation && (
        <div>
          <div className="ok-eyebrow mb-2">Motivatie</div>
          <p className="text-sm text-[#0a191a] whitespace-pre-wrap bg-[#f7f6f2] p-4">{c.motivation}</p>
        </div>
      )}
      <div>
        <div className="ok-eyebrow mb-2">CV</div>
        {c.resume_path ? (
          <a
            href={`${API}/resume/download/${c.user_id}`}
            target="_blank"
            rel="noreferrer"
            data-testid="detail-download-cv"
            className="inline-flex items-center gap-2 border border-[#0a191a] px-4 py-2 rounded-sm text-sm hover:bg-[#0a191a] hover:text-white"
          >
            <Download size={14} /> Download {c.resume_filename || "cv.pdf"}
          </a>
        ) : (
          <p className="text-sm text-[#7b8e8f]">Geen CV geupload.</p>
        )}
      </div>
      <div>
        <div className="ok-eyebrow mb-2">Sollicitaties ({c.applications?.length || 0})</div>
        {(c.applications || []).length === 0 ? (
          <p className="text-sm text-[#7b8e8f]">Geen sollicitaties.</p>
        ) : (
          <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
            {c.applications.map((a) => (
              <div key={a.id} className="p-3 flex justify-between items-center text-sm">
                <div>
                  <div className="font-semibold">{a.vacancy_title}</div>
                  <div className="text-xs text-[#4a5d5e]">{a.submitted_date?.slice(0, 10)}</div>
                </div>
                <span className="ok-chip text-xs">{a.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {(c.proposals || []).length > 0 && (
        <div>
          <div className="ok-eyebrow mb-2">Voorstellen aan inleners ({c.proposals.length})</div>
          <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
            {c.proposals.map((p) => (
              <div key={p.id} className="p-3 flex justify-between text-sm">
                <div className="text-xs text-[#4a5d5e]">{p.sent_date?.slice(0, 10)}</div>
                <span className="ok-chip text-xs">{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClientDetailBody({ c }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="ok-eyebrow mb-1">Organisatie</div>
        <Row label="Naam" value={c.organization_name} />
        <Row label="Type" value={c.organization_type} />
        <Row label="Contactpersoon" value={c.contact_name} />
        <Row label="Functietitel" value={c.job_title} />
        <Row label="E-mail" value={<a href={`mailto:${c.email}`} className="text-[#01696f] hover:underline">{c.email}</a>} />
        <Row label="Telefoon" value={c.phone} />
        <Row label="Interessegebieden" value={(c.interest_areas || []).join(", ")} />
        <Row label="Hoe gevonden" value={c.how_found_us} />
        <Row label="Aangemeld op" value={c.created_at?.slice(0, 10)} />
      </div>
      {c.message && (
        <div>
          <div className="ok-eyebrow mb-2">Bericht</div>
          <p className="text-sm bg-[#f7f6f2] p-4 whitespace-pre-wrap">{c.message}</p>
        </div>
      )}
      <div>
        <div className="ok-eyebrow mb-2">Voorgestelde kandidaten ({c.proposals?.length || 0})</div>
        {(c.proposals || []).length === 0 ? (
          <p className="text-sm text-[#7b8e8f]">Nog geen voorstellen verstuurd.</p>
        ) : (
          <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
            {c.proposals.map((p) => (
              <div key={p.id} className="p-3 flex justify-between text-sm">
                <div className="font-semibold">{p.candidate_name}</div>
                <span className="ok-chip text-xs">{p.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ApplicationDetailBody({ a }) {
  const c = a.candidate;
  const v = a.vacancy;
  return (
    <div className="space-y-6">
      <div>
        <div className="ok-eyebrow mb-1">Sollicitatie</div>
        <Row label="Status" value={a.status} />
        <Row label="Ingediend op" value={a.submitted_date?.slice(0, 19).replace("T", " ")} />
        {a.notes && <Row label="Toelichting" value={a.notes} />}
      </div>
      {v && (
        <div>
          <div className="ok-eyebrow mb-1">Vacature</div>
          <Row label="Titel" value={v.title} />
          <Row label="Locatie" value={`${v.location}, ${v.province}`} />
          <Row label="Expertise" value={v.expertise_category} />
          <Row label="Uren/week" value={v.hours_per_week} />
          <Row label="Duur" value={`${v.duration_months} mnd`} />
        </div>
      )}
      {c && (
        <div>
          <div className="ok-eyebrow mb-1">Kandidaat</div>
          <Row label="Naam" value={`${c.first_name} ${c.last_name}`} />
          <Row label="E-mail" value={<a href={`mailto:${c.email}`} className="text-[#01696f] hover:underline">{c.email}</a>} />
          <Row label="Telefoon" value={c.phone} />
          <Row label="Expertise" value={(c.expertise_categories && c.expertise_categories.length > 0) ? c.expertise_categories.join(", ") : c.expertise_category} />
          <Row label="Ervaring" value={`${c.years_experience} jaar`} />
          <Row label="Beschikbaar vanaf" value={c.available_from} />
          <Row label="Uurtarief" value={c.hourly_rate ? `€ ${c.hourly_rate}/u` : null} />
          {c.resume_path && (
            <div className="mt-3">
              <a
                href={`${API}/resume/download/${c.user_id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-[#0a191a] px-4 py-2 rounded-sm text-sm hover:bg-[#0a191a] hover:text-white"
              >
                <Download size={14} /> Download CV
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmailDetailBody({ e }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="ok-eyebrow mb-1">Metadata</div>
        <Row label="Onderwerp" value={<span className="font-semibold">{e.subject}</span>} />
        <Row label="Naar" value={<a href={`mailto:${e.to}`} className="text-[#01696f] hover:underline">{e.to}</a>} />
        <Row label="Type" value={e.kind} />
        <Row label="Verzonden op" value={e.sent_at?.slice(0, 19).replace("T", " ")} />
      </div>
      <div>
        <div className="ok-eyebrow mb-2">Inhoud</div>
        <div className="bg-[#f7f6f2] border border-[#e2e8f0] p-5 text-sm whitespace-pre-wrap text-[#0a191a]">
          {e.body}
        </div>
      </div>
      {e.metadata && Object.keys(e.metadata).length > 0 && (
        <div>
          <div className="ok-eyebrow mb-2">Extra data</div>
          <pre className="bg-[#f7f6f2] border border-[#e2e8f0] p-4 text-xs overflow-x-auto">{JSON.stringify(e.metadata, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function VacancyForm({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial || {
    title: "", description: "", location: "", province: "Noord-Holland",
    hours_per_week: 36, duration_months: 6, start_date: "", deadline: "",
    hourly_rate_max: "", hourly_rate_min: "", salary_min: "", salary_max: "",
    contract_type: "Beide", seniority: "Medior",
    expertise_category: "Beleid", knockout_criteria: "",
    preferred_criteria: "", status: "open", source: "manual",
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...f,
      hours_per_week: Number(f.hours_per_week),
      duration_months: Number(f.duration_months),
      hourly_rate_max: f.hourly_rate_max ? Number(f.hourly_rate_max) : null,
      hourly_rate_min: f.hourly_rate_min ? Number(f.hourly_rate_min) : null,
      salary_min: f.salary_min ? Number(f.salary_min) : null,
      salary_max: f.salary_max ? Number(f.salary_max) : null,
    });
  };
  const showZZP = f.contract_type === "ZZP" || f.contract_type === "Beide";
  const showSalary = f.contract_type === "Detacheren/dienstverband" || f.contract_type === "Beide";
  return (
    <form onSubmit={submit} className="border border-[#01696f] bg-[#e6f0f1]/30 p-6 mb-5 space-y-5">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">{initial ? "Vacature bewerken" : "Nieuwe vacature"}</h3>
        <button type="button" onClick={onClose} className="p-1" aria-label="Sluiten"><X size={16} /></button>
      </div>

      <VF label="Titel" required>
        <input required className="vac-input" value={f.title} onChange={(e) => set("title", e.target.value)} />
      </VF>
      <VF label="Beschrijving" required>
        <textarea required rows={3} className="vac-input" value={f.description} onChange={(e) => set("description", e.target.value)} />
      </VF>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VF label="Locatie" required>
          <input required className="vac-input" value={f.location} onChange={(e) => set("location", e.target.value)} />
        </VF>
        <VF label="Provincie">
          <select className="vac-input" value={f.province} onChange={(e) => set("province", e.target.value)}>
            {PROVINCES.map((p) => <option key={p}>{p}</option>)}
          </select>
        </VF>
        <VF label="Expertise">
          <select className="vac-input" value={f.expertise_category} onChange={(e) => set("expertise_category", e.target.value)}>
            {EXPERTISE.map((p) => <option key={p}>{p}</option>)}
          </select>
        </VF>
        <VF label="Senioriteit">
          <select className="vac-input" value={f.seniority || "Medior"} onChange={(e) => set("seniority", e.target.value)}>
            <option>Junior</option>
            <option>Medior</option>
            <option>Senior</option>
            <option>Lead</option>
          </select>
        </VF>
        <VF label="Status">
          <select className="vac-input" value={f.status} onChange={(e) => set("status", e.target.value)}>
            <option value="open">open</option>
            <option value="ingevuld">ingevuld</option>
            <option value="verlopen">verlopen</option>
          </select>
        </VF>
        <VF label="Contractvorm" required>
          <select required className="vac-input" value={f.contract_type || "Beide"} onChange={(e) => set("contract_type", e.target.value)}>
            <option value="ZZP">ZZP</option>
            <option value="Detacheren/dienstverband">Detacheren/dienstverband</option>
            <option value="Beide">Beide</option>
          </select>
        </VF>
        <VF label="Uren per week">
          <input type="number" className="vac-input" value={f.hours_per_week} onChange={(e) => set("hours_per_week", e.target.value)} />
        </VF>
        <VF label="Duur (maanden)">
          <input type="number" className="vac-input" value={f.duration_months} onChange={(e) => set("duration_months", e.target.value)} />
        </VF>
        <VF label="Startdatum">
          <input type="date" className="vac-input" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} />
        </VF>
        <VF label="Deadline reageren">
          <input type="date" className="vac-input" value={f.deadline || ""} onChange={(e) => set("deadline", e.target.value)} />
        </VF>
      </div>

      {showZZP && (
        <div className="border border-[#e2e8f0] p-4 bg-white">
          <div className="ok-eyebrow mb-3">ZZP tariefrange (€/uur)</div>
          <div className="grid grid-cols-2 gap-3">
            <VF label="Minimum tarief">
              <input type="number" step="0.5" className="vac-input" placeholder="bv. 90" value={f.hourly_rate_min || ""} onChange={(e) => set("hourly_rate_min", e.target.value)} />
            </VF>
            <VF label="Maximum tarief">
              <input type="number" step="0.5" className="vac-input" placeholder="bv. 130" value={f.hourly_rate_max || ""} onChange={(e) => set("hourly_rate_max", e.target.value)} />
            </VF>
          </div>
        </div>
      )}
      {showSalary && (
        <div className="border border-[#e2e8f0] p-4 bg-white">
          <div className="ok-eyebrow mb-3">Detacheren / dienstverband salarisrange (€/maand bruto)</div>
          <div className="grid grid-cols-2 gap-3">
            <VF label="Minimum salaris">
              <input type="number" className="vac-input" placeholder="bv. 4500" value={f.salary_min || ""} onChange={(e) => set("salary_min", e.target.value)} />
            </VF>
            <VF label="Maximum salaris">
              <input type="number" className="vac-input" placeholder="bv. 6500" value={f.salary_max || ""} onChange={(e) => set("salary_max", e.target.value)} />
            </VF>
          </div>
        </div>
      )}

      <VF label="Knock-out criteria">
        <textarea rows={2} className="vac-input" placeholder="Eisen waar kandidaat aan moet voldoen" value={f.knockout_criteria} onChange={(e) => set("knockout_criteria", e.target.value)} />
      </VF>
      <VF label="Gunningscriteria">
        <textarea rows={2} className="vac-input" placeholder="Pluspunten / wensen" value={f.preferred_criteria} onChange={(e) => set("preferred_criteria", e.target.value)} />
      </VF>

      <button data-testid="vac-save" className="bg-[#01696f] text-white px-5 py-2.5 rounded-sm hover:bg-[#015257]">Opslaan</button>
      <style>{`.vac-input { width: 100%; padding: 0.5rem 0.625rem; border: 1px solid #e2e8f0; background: #fff; font-size: 0.875rem; border-radius: 2px; } .vac-input:focus { outline: 2px solid #01696f; outline-offset: 1px; }`}</style>
    </form>
  );
}

function VF({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[#4a5d5e] mb-1.5">
        {label}{required && <span className="text-[#d32f2f]"> *</span>}
      </label>
      {children}
    </div>
  );
}
